import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import RMModal from 'components/modal/RMModal';
import { EReportTypes } from 'enums/EIconPanels';
import Konva from 'konva';
import React, { useRef, useState } from 'react';
import { createCanvasDesignPDFReport } from './ExperimentDesignReport';
import {
  ExperimentReportOptions,
  ExperimentReportOptionsDTO,
  CaptureProfileOption,
} from './ExperimentReportOptions';
import { SingleDropReportOptions } from './SingleDropReportOptions';
import { generateWellReportPDF } from './WellReportPDF';
import { ImageReportOptions } from './ImageReportOptions';
import './experimentReports.css';
import { InstantDropReport } from './InstantDropReport';
import { ExportScoresOptions } from './ExportScoresOptions';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { IngredientsReportOptions } from './IngredientsReportOptions';
import SynchTripsExpReportOptions from 'components/synchrotron/SynchTripsExpReportOptions';
import PlateService from 'business/services/PlateService';
import { generateScreenStockReportPDF } from './ScreenStockReport';
import { GetPlateIngredientsService } from 'business/services/IngredientService';
import { PlateIngredientDTO } from 'business/models/Models';
import { WellNavigationDetails } from 'components/Experiment/constants';
import { FmlxModal, FmlxBanner } from 'fmlx-common-ui';
import { useWellSelectionDetails } from 'redux/hooks/experiment';
import { getDesignImageURL } from './CanvasReportUtils';
import { useExperimentReady } from './hooks';
import { useRMSelector } from 'redux/hooks';
import { selectIsFetchingExperimentData } from 'redux/selectors/ExperimentStateSelector';
import { getScreenNamesFromLayers } from './utils';

type ExperimentReportsDialogueProps = {
  reportType: string;
  setReportType: (e: string) => void;
  canvasRef: Konva.Stage;
  docData: ExperimentDtoClient;
  platePadding: number;
  selectedWell: WellNavigationDetails[];
  captureProfiles?: CaptureProfileOption[];
  onPopupBlocked?: () => void;
  onGenerateExperimentReport?: (
    reportOptions: ExperimentReportOptionsDTO,
    saveReport: boolean,
    onPopupBlocked?: () => void,
  ) => Promise<void>;
  onStartBackgroundReport?: (
    reportOptions: ExperimentReportOptionsDTO,
    saveReport: boolean,
  ) => void | Promise<void>;
  runReportInBackground?: boolean;
};
export const ExperimentReportsDialogue = ({
  reportType,
  setReportType,
  canvasRef,
  docData,
  platePadding,
  selectedWell,
  captureProfiles,
  onPopupBlocked,
  onGenerateExperimentReport,
  onStartBackgroundReport,
  runReportInBackground,
}: ExperimentReportsDialogueProps) => {
  const reportOptionRef = useRef(null);
  const [okayDisabled, setOkayDisabled] = useState(false);
  const wellSelectionDetails = useWellSelectionDetails();
  const populatingWellList = useExperimentReady(docData);
  const isFetchingExpData = useRMSelector(selectIsFetchingExperimentData);

  function getDialogueClass() {
    if (docData.isDirty) {
      return 'modal-fullw';
    } else if (reportType === EReportTypes.SingleDropReport) {
      return 'modal-singleDropReportWidth';
    } else if (
      reportType === EReportTypes.InstantDropReport ||
      reportType === EReportTypes.DesignReport ||
      reportType === EReportTypes.WellReport ||
      reportType === EReportTypes.ScreenStockReport
    ) {
      return 'modal-instantDropReportWidth';
    } else if (reportType === EReportTypes.IngredientReport) {
      return 'w-[841px] max-w-[900px]';
    } else {
      return 'modal-fullw';
    }
  }

  const getTitleColor = () => {
    if (reportType == EReportTypes.IngredientReport) {
      return '#1B1B1B';
    } else {
      return '#F7A246';
    }
  };

  function getModalBody() {
    let body;
    if (docData.isDirty) {
      return (body = 'Please save experiment before generating report.');
    }
    switch (reportType) {
      case EReportTypes.InstantDropReport:
        body = (
          <InstantDropReport
            docData={docData}
            ref={reportOptionRef}
            selectedWell={selectedWell}
            setOkayDisabled={setOkayDisabled}
          />
        );
        break;
      case EReportTypes.DesignReport:
        if (isFetchingExpData || docData.layers?.length > 0) {
          body = (
            <div
              id="experiment-reports-dialogue-modal-body-design-report-wrapper"
              className="idr-wrapper-content"
            >
              <div
                id="experiment-reports-dialogue-modal-body-design-report-content"
                className="idr-content"
              >
                Please select the design report action.
              </div>
            </div>
          );
        } else {
          setReportType('');
          showAlertSnackBar(
            EAlertType.WARNING,
            ESnackBarPosition.TOP,
            'Document is empty.',
          );
        }
        break;
      case EReportTypes.ScreenStockReport:
        if (isFetchingExpData || docData.layers?.length > 0) {
          body = (
            <div
              id="experiment-reports-dialogue-modal-body-screen-stock-report-wrapper"
              className="idr-wrapper-content"
            >
              <div
                id="experiment-reports-dialogue-modal-body-screen-stock-report-content"
                className="idr-content"
              >
                Please select the screen stock report action.
              </div>
            </div>
          );
        } else {
          setReportType('');
          showAlertSnackBar(
            EAlertType.WARNING,
            ESnackBarPosition.TOP,
            'Document is empty.',
          );
        }
        break;
      case EReportTypes.ExperimentReport:
        body = (
          <ExperimentReportOptions
            docData={docData}
            ref={reportOptionRef}
            canvasRef={canvasRef}
            platePadding={platePadding}
            captureProfiles={captureProfiles}
            onFetchingData={setOkayDisabled}
            onGenerateReport={onGenerateExperimentReport}
          />
        );
        break;
      case EReportTypes.SingleDropReport:
        if (docData.layers?.length === 0 || docData.inspections?.length === 0) {
          setReportType('');
          showAlertSnackBar(
            EAlertType.WARNING,
            ESnackBarPosition.TOP,
            'This plate does not have any inspection.',
          );
        } else {
          body = (
            <SingleDropReportOptions
              docData={docData}
              ref={reportOptionRef}
              canvasRef={canvasRef}
              platePadding={platePadding}
            />
          );
        }
        break;
      case EReportTypes.ImageReport:
        if (docData.inspections[0].groupedInspections.length > 0) {
          body = (
            <ImageReportOptions
              docData={docData}
              ref={reportOptionRef}
              canvasRef={canvasRef}
              platePadding={platePadding}
            />
          );
        } else {
          setReportType('');
          showAlertSnackBar(
            EAlertType.WARNING,
            ESnackBarPosition.TOP,
            'This plate does not have any inspection.',
          );
        }
        break;
      case EReportTypes.WellReport:
        body = (
          <div
            id="experiment-reports-dialogue-modal-body-well-report-wrapper"
            className="idr-wrapper-content"
            style={{ border: 'none' }}
          >
            <FmlxBanner
              id="experiment-reports-dialogue-modal-body-well-report-banner"
              variant="warning"
              title="This process will take a few seconds depending on network speed."
              show={selectedWell.length > 96}
              hideCloseButton
            />
            <div
              id="experiment-reports-dialogue-modal-body-well-report-content"
              className="idr-content"
            >
              Please select the well report action.
            </div>
          </div>
        );
        break;
      case EReportTypes.ScoreReport:
        body = (
          <ExportScoresOptions
            docData={docData}
            ref={reportOptionRef}
            canvasRef={canvasRef}
            platePadding={platePadding}
          />
        );
        break;
      case EReportTypes.SynchrotronTripReport:
        body = (
          <SynchTripsExpReportOptions
            expId={docData?.experimentId}
            closeReportOptionsModal={() => setReportType('')}
          />
        );
        // <SynchTripReportOptions expId={docData?.experimentId} synchTripId={selectedTripId} closeReportOptionsModal={()=> setReportType('')} />
        break;
      case EReportTypes.IngredientReport:
        body = (
          <IngredientsReportOptions
            docData={docData}
            ref={reportOptionRef}
            canvasRef={canvasRef}
            platePadding={platePadding}
            selectedWells={selectedWell}
          />
        );
        break;
    }
    return body;
  }

  async function previewReport() {
    await generateReport(false);
  }

  async function saveReport() {
    await generateReport(true);
  }

  async function generateReport(saveReport: boolean) {
    let closeModal = true;
    switch (reportType) {
      case EReportTypes.DesignReport:
        await createExperimentDesignReport(saveReport, onPopupBlocked);
        break;
      case EReportTypes.ScreenStockReport:
        const screen = await new PlateService().getScreenByExpId(docData.experimentId);
        const plates: PlateIngredientDTO[] = [];
        for (const plt of screen.plateDto) {
          plates.push(await GetPlateIngredientsService(plt.plateId));
        }
        await generateScreenStockReportPDF(
          docData.experimentId,
          screen,
          plates,
          saveReport,
          onPopupBlocked,
        );
        break;
      case EReportTypes.ExperimentReport:
        if (
          runReportInBackground &&
          onStartBackgroundReport &&
          reportOptionRef.current?.getReportOptions
        ) {
          onStartBackgroundReport(reportOptionRef.current.getReportOptions(), saveReport);
          setReportType('');
          return;
        }
        await reportOptionRef.current.generateAction(saveReport, onPopupBlocked);
        break;
      case EReportTypes.InstantDropReport:
        await reportOptionRef.current.generateAction(saveReport, onPopupBlocked);
        break;
      case EReportTypes.SingleDropReport:
        await reportOptionRef.current.generateAction(saveReport, onPopupBlocked);
        break;
      case EReportTypes.WellReport:
        const wellReportDocData = await populatingWellList;
        await generateWellReportPDF(
          wellReportDocData,
          wellSelectionDetails,
          saveReport,
          onPopupBlocked,
        );
        break;
      case EReportTypes.ImageReport:
        closeModal = await reportOptionRef.current.generateAction(
          saveReport,
          onPopupBlocked,
        );
        break;
      case EReportTypes.ScoreReport:
        await reportOptionRef.current.generateAction();
        break;
      case EReportTypes.IngredientReport:
        closeModal = await reportOptionRef.current.generateAction(
          saveReport,
          onPopupBlocked,
        );
        break;
    }
    if (closeModal === true) {
      setReportType('');
    }
  }

  function getHeading() {
    let header;
    if (docData.isDirty) {
      header = 'Warning';
      return header;
    }
    switch (reportType) {
      case EReportTypes.InstantDropReport:
        header = 'Instant Drop Report';
        break;
      case EReportTypes.ExperimentReport:
        header = 'Experiment Report Options';
        break;
      case EReportTypes.ImageReport:
        header = 'Image Report Options';
        break;
      case EReportTypes.SingleDropReport:
        header = 'Single Drop Report Options';
        break;
      case EReportTypes.WellReport:
        header = 'Well Report';
        break;
      case EReportTypes.ScoreReport:
        header = 'Export Score Options';
        break;
      case EReportTypes.DesignReport:
        header = 'Design Report';
        break;
      case EReportTypes.ScreenStockReport:
        header = 'Screen Stock Report';
        break;
      case EReportTypes.SynchrotronTripReport:
        header = 'Synchrotron Trip Report';
        break;
      case EReportTypes.IngredientReport:
        header = 'Ingredient Report Options';
        break;
    }
    return header;
  }

  function getOkayButtonText() {
    let OkButtonText = 'Preview';
    if (reportType === EReportTypes.ScoreReport) {
      OkButtonText = 'Export';
    }
    return OkButtonText;
  }

  function getSaveButtonAvailable() {
    let saveAvailable = true;
    if (docData.isDirty) {
      saveAvailable = false;
    }
    if (reportType === EReportTypes.ScoreReport) {
      saveAvailable = false;
    }
    return saveAvailable;
  }

  const getModalSize = () => {
    if (
      reportType === EReportTypes.IngredientReport ||
      reportType === EReportTypes.ImageReport ||
      reportType === EReportTypes.SingleDropReport
    ) {
      return FmlxModal.Size.EXTRA_LARGE;
    }
    return FmlxModal.Size.MEDIUM;
  };

  async function createExperimentDesignReport(
    saveReport: boolean,
    onPopupBlocked?: () => void,
  ) {
    const completeDocData = await populatingWellList;
    const isMultiPlateExperiment = completeDocData.plateDto.length > 1 ? true : false;
    const selectedPlate = completeDocData.plateDto.find(
      (p) => p.plateId == selectedWell[0].plateID,
    );
    const imgUrl = getDesignImageURL(canvasRef, selectedPlate);
    const screenNames = getScreenNamesFromLayers(completeDocData.layers);
    await createCanvasDesignPDFReport(
      imgUrl,
      selectedPlate.plateId,
      selectedPlate.plateBarName,
      completeDocData.experimentId,
      completeDocData.expName,
      saveReport,
      isMultiPlateExperiment,
      screenNames,
      onPopupBlocked,
    );
  }

  {
    if (!getModalBody()) {
      return null;
    }
  }

  return (
    <RMModal
      id="experimentreportdialogue-wrapper"
      showModal={reportType != ''}
      setShowModal={() => setReportType('')}
      body={getModalBody()}
      onOk={previewReport}
      onSave={saveReport}
      isCancelOnly={docData.isDirty}
      okButtonText={getOkayButtonText()}
      saveButtonText="Save"
      noButtonText="Close"
      okayDisabled={okayDisabled}
      saveDisabled={okayDisabled}
      isSaveAvailable={getSaveButtonAvailable()}
      dialogClassName={getDialogueClass()}
      heading={getHeading()}
      showButtons={reportType !== EReportTypes.SynchrotronTripReport}
      hideFooter={reportType === EReportTypes.SynchrotronTripReport}
      titleClr={getTitleColor()}
      size={getModalSize()}
    />
  );
};
